import React from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import {
  ConversationMetrics,
  EmotionAnalysis,
  FillerStats,
  KeywordTheme,
  MeetingInsights,
  PresentationDeck,
  ProsodyAnalysis,
  SlideDeckSummary,
  SlideTimelineEntry,
  SpeechSegment,
  VideoAnalysisMeta,
} from '../../../services/api.service';
import { formatSecondsShort, formatSpeakerFullName, stripSpeakerOrgSuffix } from '../utils/audio-segments';
import { langLabel } from '../utils/speaker-language';
import SpeakerDonutChart from './speaker-donut-chart.component';
import MeetingAnalyticsPanel from './meeting-analytics-panel.component';
import VideoAnalyticsHub from './video-analytics/video-analytics-hub.component';
import MeetingAudioPlayer from './meeting-audio-player.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

export type SpeakerTimelineItem = {
  id: number;
  name: string;
  label: string;
  language?: string;
  color: string;
  segments: { startPct: number; endPct: number; startSec: number; endSec: number }[];
  totalSpeakingSec: number;
};

type Props = {
  isVideoMeeting: boolean;
  videoUrl: string;
  audioUrl: string;
  meetingDateLabel: string;
  segmentsCount: number;
  recordingDurationSec: number;
  onTimeChange: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  seekRef?: React.MutableRefObject<((time: number) => void) | null>;
  togglePlayPauseRef?: React.MutableRefObject<(() => void) | null>;
  videoAnalysis?: VideoAnalysisMeta | null;
  speakerTimelines: SpeakerTimelineItem[];
  segments: SpeechSegment[];
  currentAudioTime: number;
  isVideoPlaying?: boolean;
  statsTotalSec: number;
  totalSpeakingSec: number;
  participantsCount: number;
  donutSlices: { label: string; color: string; percent: number }[];
  speakingPercentByName: Record<string, number>;
  pauseStats: { unmarkedSec: number; unmarkedPct: number; coveredSec: number };
  speechCoverage: { coveredSec: number; coveragePct: number; isLow: boolean };
  insights: MeetingInsights | null;
  slideTimeline: SlideTimelineEntry[];
  presentationSlides: SlideTimelineEntry[];
  presentationDecks: PresentationDeck[];
  slideDeckSummary: SlideDeckSummary | null;
  meetingId: string;
  getFrameUrl: (meetingId: string, index: number, crop?: boolean) => string;
  onSummarizeSlides?: () => Promise<void>;
  onGeneratePptx?: () => Promise<void>;
  summarizingSlides: boolean;
  generatingPptx: boolean;
  insightsLoading: boolean;
  fillingGaps: boolean;
  pdfLoading: boolean;
  onSeek: (seconds: number) => void;
  onSpeakerPlay?: (speakerName: string) => void;
  soloSpeakerName?: string | null;
  onExitSolo?: () => void;
  onTogglePlayPause?: () => void;
  onFillGaps: () => void;
  onDownloadStatsPdf: () => void;
  onRefreshInsights: () => void;
  onOpenThemeReplicas?: (theme: KeywordTheme) => void;
  onOpenKeywordReplicas?: (word: string) => void;
  /** Задача F1: голосовая аналитика (H1/B1/B2) — null у встреч, обработанных до этих задач. */
  conversationMetrics?: ConversationMetrics | null;
  fillerStats?: FillerStats | null;
  emotionAnalysis?: EmotionAnalysis | null;
  prosodyAnalysis?: ProsodyAnalysis | null;
  recomputingAnalytics?: boolean;
  onRecomputeAnalytics?: () => void;
};

const MeetingAnalyticsTab: React.FC<Props> = ({
  isVideoMeeting,
  videoUrl,
  audioUrl,
  meetingDateLabel,
  segmentsCount,
  recordingDurationSec,
  onTimeChange,
  onDurationChange,
  onPlayingChange,
  seekRef,
  togglePlayPauseRef,
  videoAnalysis,
  speakerTimelines,
  segments,
  currentAudioTime,
  isVideoPlaying = false,
  statsTotalSec,
  totalSpeakingSec,
  participantsCount,
  donutSlices,
  speakingPercentByName,
  pauseStats,
  speechCoverage,
  insights,
  slideTimeline,
  presentationSlides,
  presentationDecks,
  slideDeckSummary,
  meetingId,
  getFrameUrl,
  onSummarizeSlides,
  onGeneratePptx,
  summarizingSlides,
  generatingPptx,
  insightsLoading,
  fillingGaps,
  pdfLoading,
  onSeek,
  onSpeakerPlay,
  soloSpeakerName = null,
  onExitSolo,
  onTogglePlayPause,
  onFillGaps,
  onDownloadStatsPdf,
  onRefreshInsights,
  onOpenThemeReplicas,
  onOpenKeywordReplicas,
  conversationMetrics = null,
  fillerStats = null,
  emotionAnalysis = null,
  prosodyAnalysis = null,
  recomputingAnalytics = false,
  onRecomputeAnalytics,
}) => {
  const { t } = useTranslation();
  const framesAnalyzed = videoAnalysis?.frames_analyzed ?? slideTimeline.length;
  const hasVlmData = isVideoMeeting && (framesAnalyzed > 0 || (slideTimeline?.length ?? 0) > 0);
  const hasSpeechData = speakerTimelines.length > 0 || segments.length > 0;
  const hasMedia = (isVideoMeeting && !!videoUrl) || (!isVideoMeeting && !!audioUrl);

  if (!hasSpeechData && !hasVlmData && !hasMedia) {
    return (
      <p className={cn('empty')}>
        Нет данных для аналитики — дождитесь завершения обработки встречи.
      </p>
    );
  }

  return (
    <div className={cn('analytics-tab')}>
      <div className={cn('analytics-tab-header')}>
        <h3 className={cn('analytics-tab-title')}>{t('file.analytics')}</h3>
        {isVideoMeeting ? (
          <div className={cn('analytics-vlm-banner')}>
            <span className={cn('analytics-vlm-badge')}>ИИ · VLM</span>
            <p className={cn('analytics-vlm-text')}>{t('file.analytics-video-vlm-note')}</p>
            {hasVlmData && (
              <p className={cn('analytics-vlm-meta')}>
                Проанализировано кадров: {framesAnalyzed}
                {(slideTimeline?.length ?? 0) > 0 && ` · слайдов и сцен: ${slideTimeline.length}`}
              </p>
            )}
          </div>
        ) : (
          <p className={cn('analytics-tab-note')}>{t('file.analytics-audio-note')}</p>
        )}
      </div>

      {hasMedia && (
        <div className={cn('analytics-player')}>
          <div className={cn('analytics-player-meta')}>
            {meetingDateLabel && <span>{meetingDateLabel}</span>}
            <span>{participantsCount} участников</span>
            <span>{segmentsCount} реплик</span>
          </div>
          {isVideoMeeting && videoUrl && (
            <VideoAnalyticsHub
              videoUrl={videoUrl}
              durationSec={recordingDurationSec}
              currentTime={currentAudioTime}
              isPlaying={isVideoPlaying}
              segments={segments}
              insights={insights}
              slideTimeline={slideTimeline}
              presentationSlides={presentationSlides}
              presentationDecks={presentationDecks}
              slideDeckSummary={slideDeckSummary}
              videoAnalysis={videoAnalysis}
              meetingId={meetingId}
              getFrameUrl={getFrameUrl}
              onTimeChange={onTimeChange}
              onDurationChange={onDurationChange}
              onPlayingChange={onPlayingChange}
              onSeek={onSeek}
              onTogglePlayPause={onTogglePlayPause}
              seekRef={seekRef}
              togglePlayPauseRef={togglePlayPauseRef}
              onSummarizeSlides={onSummarizeSlides}
              onGeneratePptx={onGeneratePptx}
              summarizing={summarizingSlides}
              generatingPptx={generatingPptx}
            />
          )}
          {!isVideoMeeting && audioUrl && (
            <MeetingAudioPlayer
              audioUrl={audioUrl}
              meetingId={meetingId}
              segments={segments}
              speakerTimelines={speakerTimelines}
              durationSec={recordingDurationSec}
              soloSpeakerName={soloSpeakerName}
              onExitSolo={onExitSolo}
              onTimeChange={onTimeChange}
              onDurationChange={onDurationChange}
              seekRef={seekRef}
              togglePlayPauseRef={togglePlayPauseRef}
            />
          )}
        </div>
      )}

      {speakerTimelines.length > 0 && (
        <div className={cn('audio-tracks')}>
          <div className={cn('audio-tracks-title')}>
            <span>Спикеры</span>
            <span className={cn('audio-tracks-hint')}> · клик по имени — solo-режим</span>
          </div>

          <div className={cn('audio-speakers')}>
            {speakerTimelines.map(speaker => {
              const isSolo = soloSpeakerName != null && (
                soloSpeakerName === speaker.name
                || speaker.name.toLowerCase().includes(soloSpeakerName.toLowerCase())
              );
              return (
              <div key={speaker.id} className={cn('audio-speaker', { solo: isSolo })}>
                <div
                  className={cn('audio-speaker-label')}
                  role="button"
                  tabIndex={0}
                  title="Клик — воспроизвести только этого спикера"
                  onClick={() => onSpeakerPlay?.(speaker.name)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSpeakerPlay?.(speaker.name);
                    }
                  }}
                >
                  <span
                    className={cn('audio-speaker-dot')}
                    style={{ backgroundColor: speaker.color }}
                  />
                  <span
                    className={cn('audio-speaker-name')}
                    style={{ color: speaker.color }}
                    title={stripSpeakerOrgSuffix(speaker.name)}
                  >
                    {formatSpeakerFullName(speaker.name)}
                  </span>
                  {speaker.language && (
                    <span className={cn('audio-speaker-lang')}>{langLabel(speaker.language)}</span>
                  )}
                </div>
                <div className={cn('audio-speaker__timeline-wrapper')}>
                  {speaker.segments.map((seg, i) => {
                    const isActive = currentAudioTime >= seg.startSec && currentAudioTime < seg.endSec;
                    const tooltip = `${formatSecondsShort(seg.startSec)} → ${formatSecondsShort(seg.endSec)}`;
                    return (
                      <div
                        key={i}
                        role="button"
                        tabIndex={0}
                        title={tooltip}
                        className={cn('audio-speaker__timeline-segment', { active: isActive })}
                        style={{
                          left: `${seg.startPct}%`,
                          width: `${Math.max(seg.endPct - seg.startPct, 0.5)}%`,
                          backgroundColor: speaker.color,
                        }}
                        onClick={() => onSeek(seg.startSec)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSeek(seg.startSec);
                          }
                        }}
                      >
                        <span className={cn('audio-speaker__timeline-tooltip')}>{tooltip}</span>
                      </div>
                    );
                  })}
                </div>
                <div className={cn('audio-speaker-meta')}>
                  <span className={cn('audio-speaker-duration')}>
                    {formatSecondsShort(speaker.totalSpeakingSec)}
                  </span>
                  <span className={cn('audio-speaker-percent')}>
                    {speakingPercentByName[speaker.name] ?? 0}%
                  </span>
                </div>
              </div>
            );
            })}

            <div className={cn('audio-speaker', { pause: true })}>
              <div className={cn('audio-speaker-label')}>
                <span
                  className={cn('audio-speaker-dot')}
                  style={{ backgroundColor: 'var(--color-border-secondary)' }}
                />
                <span
                  className={cn('audio-speaker-name', { pause: true })}
                  title="Время без сегментов диаризации — часто это не тишина, а неразмеченная речь"
                >
                  Не размечено
                </span>
              </div>
              <div className={cn('audio-speaker__timeline-wrapper')}>
                <div
                  className={cn('audio-speaker__timeline-pause-fill')}
                  style={{ width: `${Math.max(pauseStats.unmarkedPct, 0.5)}%` }}
                />
              </div>
              <div className={cn('audio-speaker-meta')}>
                <span className={cn('audio-speaker-duration')}>
                  {formatSecondsShort(pauseStats.unmarkedSec)}
                </span>
                <span className={cn('audio-speaker-percent')}>{pauseStats.unmarkedPct}%</span>
              </div>
            </div>

            <div className={cn('audio-speaker', { total: true })}>
              <div className={cn('audio-speaker-label')}>
                <span className={cn('audio-speaker-name', { total: true })}>Итого</span>
              </div>
              <div className={cn('audio-speaker__timeline-wrapper', { total: true })}>
                <div className={cn('audio-speaker__timeline-total-bar')} />
              </div>
              <div className={cn('audio-speaker-meta')}>
                <span className={cn('audio-speaker-duration')}>
                  {formatSecondsShort(statsTotalSec)}
                </span>
              </div>
            </div>
          </div>

          <div className={cn('audio-stats-footer')}>
            <span className={cn('audio-stats-footer-label')}>Размечено / Запись</span>
            <span className={cn('audio-stats-footer-value')}>
              {formatSecondsShort(pauseStats.coveredSec)} / {formatSecondsShort(statsTotalSec)}
              {' '}({speechCoverage.coveragePct}%)
            </span>
          </div>
          {speechCoverage.isLow && (
            <p className={cn('audio-stats-coverage-warn')}>
              Распознано только {speechCoverage.coveragePct}% записи (
              {formatSecondsShort(pauseStats.coveredSec)} из {formatSecondsShort(statsTotalSec)}).
              Распознано не всё: тихая речь, казахский и удалённые участники Teams часто пропускаются.
              {' '}
              <button
                type="button"
                className={cn('download-btn', { secondary: true })}
                onClick={onFillGaps}
                disabled={fillingGaps}
                style={{ marginTop: 8 }}
              >
                {fillingGaps ? 'Дозаполнение…' : 'Дозаполнить пропуски (Qwen)'}
              </button>
            </p>
          )}
        </div>
      )}

      {hasSpeechData && (
      <div className={cn('audio-stats')}>
        <div className={cn('audio-stats-header')}>
          <span className={cn('audio-stats-header-title')}>Статистика встречи</span>
          <button
            type="button"
            className={cn('download-btn', { secondary: true })}
            onClick={onDownloadStatsPdf}
            disabled={pdfLoading || !segments.length}
          >
            {pdfLoading ? 'PDF…' : 'Скачать PDF'}
          </button>
        </div>

        <div className={cn('audio-stats-section')}>
          <div className={cn('audio-stats-section-title')}>Распределение</div>
          <SpeakerDonutChart
            slices={donutSlices}
            centerValue={formatSecondsShort(totalSpeakingSec)}
            centerLabel="речь"
          />
        </div>

        <div className={cn('audio-stats-cards')}>
          <div className={cn('audio-stats-card')}>
            <span className={cn('audio-stats-card-value')}>{participantsCount}</span>
            <span className={cn('audio-stats-card-label')}>Участников</span>
          </div>
          <div className={cn('audio-stats-card')}>
            <span className={cn('audio-stats-card-value')}>
              {formatSecondsShort(statsTotalSec)}
            </span>
            <span className={cn('audio-stats-card-label')}>Длительность</span>
          </div>
          <div className={cn('audio-stats-card')}>
            <span className={cn('audio-stats-card-value')}>{speechCoverage.coveragePct}%</span>
            <span className={cn('audio-stats-card-label')}>Размечено</span>
          </div>
        </div>

        <MeetingAnalyticsPanel
          segments={segments}
          speakerTimelines={speakerTimelines.map(s => ({ name: s.name, color: s.color }))}
          durationSec={statsTotalSec}
          insights={insights}
          slideTimeline={slideTimeline}
          isVideoMeeting={isVideoMeeting}
          insightsLoading={insightsLoading}
          onSeek={onSeek}
          onRefreshInsights={onRefreshInsights}
          onOpenThemeReplicas={onOpenThemeReplicas}
          onOpenKeywordReplicas={onOpenKeywordReplicas}
          conversationMetrics={conversationMetrics}
          fillerStats={fillerStats}
          emotionAnalysis={emotionAnalysis}
          prosodyAnalysis={prosodyAnalysis}
          recomputingAnalytics={recomputingAnalytics}
          onRecomputeAnalytics={onRecomputeAnalytics}
        />
      </div>
      )}
    </div>
  );
};

export default MeetingAnalyticsTab;
